import React from 'react';

function AdminPartnerships({ supabaseClient }) {
    const [partnerships, setPartnerships] = React.useState([]);
    const [isLoading, setIsLoading] = React.useState(true);
    const [error, setError] = React.useState(null);
    const [filter, setFilter] = React.useState('all'); // all, pending, approved, rejected
    const [selectedPartnership, setSelectedPartnership] = React.useState(null);
    const [showDetailsModal, setShowDetailsModal] = React.useState(false);
    const [showUpdateModal, setShowUpdateModal] = React.useState(false);
    const [updateStatus, setUpdateStatus] = React.useState('');
    const [updateNotes, setUpdateNotes] = React.useState('');

    React.useEffect(() => {
        fetchPartnerships();
    }, [filter, supabaseClient]);

    const fetchPartnerships = async () => {
        if (!supabaseClient) return;
        
        setIsLoading(true);
        setError(null);        try {
            // First try to fetch from a partnerships table if it exists
            let { data: partnershipData, error: partnershipError } = await supabaseClient
                .from('partnerships')
                .select('*')
                .order('created_at', { ascending: false });

            // If that fails, try the messages table for partnership inquiries
            if (partnershipError && partnershipError.code === '42P01') {
                let query = supabaseClient
                    .from('messages')
                    .select('*')
                    .ilike('subject', '%Partnership Inquiry%')
                    .order('created_at', { ascending: false });
                
                const { data, error } = await query;
                
                if (error) {
                    // If messages table also fails, try volunteer_applications as final fallback
                    let fallbackQuery = supabaseClient
                        .from('volunteer_applications')
                        .select('*')
                        .order('created_at', { ascending: false });
                    
                    if (filter !== 'all') {
                        fallbackQuery = fallbackQuery.eq('status', filter);
                    }
                    
                    const { data: fallbackData, error: fallbackError } = await fallbackQuery;
                    
                    if (fallbackError) throw fallbackError;
                    
                    setPartnerships(fallbackData || []);
                    setIsLoading(false);
                    return;
                }
                
                // Process messages data to extract partnership info
                const processedData = data.map(msg => {
                    let parsedMessage = {};
                    try {
                        parsedMessage = typeof msg.message === 'string' ? JSON.parse(msg.message) : msg.message;
                    } catch (e) {
                        parsedMessage = { message: msg.message };
                    }
                    
                    return {
                        id: msg.id,
                        created_at: msg.created_at,
                        status: msg.is_read ? 'reviewed' : 'pending',
                        company_name: parsedMessage.company_name || 'Unknown',
                        contact_name: parsedMessage.contact_name || msg.name,
                        email: parsedMessage.email || msg.email,
                        phone: parsedMessage.phone,
                        company_size: parsedMessage.company_size,
                        interests: parsedMessage.interests,
                        message: parsedMessage.message,
                        selected_tier: parsedMessage.selected_tier,
                        form_type: parsedMessage.formType || 'partnership',
                        admin_notes: msg.admin_notes,
                        original_subject: msg.subject
                    };
                });
                
                // Filter processed data if needed
                const filteredData = filter !== 'all' 
                    ? processedData.filter(p => p.status === filter)
                    : processedData;
                
                setPartnerships(filteredData || []);
                setIsLoading(false);
                return;
            } else if (partnershipError) {
                throw partnershipError;
            }

            // If we get here, the partnerships table exists
            if (filter !== 'all') {
                partnershipData = partnershipData.filter(p => p.status === filter);
            }
            
            setPartnerships(partnershipData || []);
        } catch (err) {
            console.error('Error fetching partnerships:', err);
            setError('Failed to load partnerships. Please try again.');
        } finally {
            setIsLoading(false);
        }
    };

    const handleViewDetails = (partnership) => {
        setSelectedPartnership(partnership);
        setShowDetailsModal(true);
    };

    const handleCloseDetailsModal = () => {
        setShowDetailsModal(false);
    };

    const handleOpenUpdateModal = (partnership) => {
        setSelectedPartnership(partnership);
        setUpdateStatus(partnership.status || 'pending');
        setUpdateNotes(partnership.admin_notes || '');
        setShowUpdateModal(true);
    };

    const handleCloseUpdateModal = () => {
        setShowUpdateModal(false);
    };    const handleUpdatePartnership = async (e) => {
        e.preventDefault();
        
        if (!selectedPartnership || !supabaseClient) return;
        
        try {
            // Determine which table to update
            const tableName = await determineTableName();
            
            let updateData = {};
            
            if (tableName === 'messages') {
                // For messages table, update the is_read status and admin_notes
                updateData = {
                    is_read: updateStatus === 'reviewed' || updateStatus === 'approved',
                    admin_notes: updateNotes,
                    updated_at: new Date().toISOString()
                };
            } else {
                // For partnerships or volunteer_applications tables
                updateData = {
                    status: updateStatus,
                    admin_notes: updateNotes,
                    updated_at: new Date().toISOString()
                };
            }
            
            const { error } = await supabaseClient
                .from(tableName)
                .update(updateData)
                .eq('id', selectedPartnership.id);
            
            if (error) throw error;
            
            // Update the partnerships array locally
            setPartnerships(partnerships.map(partnership => 
                partnership.id === selectedPartnership.id ? 
                { 
                    ...partnership, 
                    status: tableName === 'messages' ? 
                        (updateStatus === 'reviewed' || updateStatus === 'approved' ? 'reviewed' : 'pending') : 
                        updateStatus,
                    admin_notes: updateNotes 
                } : 
                partnership
            ));
            
            handleCloseUpdateModal();
        } catch (err) {
            console.error('Error updating partnership:', err);
            alert('Failed to update partnership. Please try again.');
        }
    };const determineTableName = async () => {
        // Check if partnerships table exists
        const { error: partnershipTableError } = await supabaseClient
            .from('partnerships')
            .select('id')
            .limit(1);
            
        if (!partnershipTableError) {
            return 'partnerships';
        }
            
        // Check if messages table exists (for partnership inquiries)
        const { error: messagesTableError } = await supabaseClient
            .from('messages')
            .select('id')
            .limit(1);
            
        if (!messagesTableError) {
            return 'messages';
        }
            
        // Use volunteer_applications as final fallback
        return 'volunteer_applications';
    };

    const handleDeletePartnership = async (partnershipId) => {
        if (!supabaseClient) return;
        
        if (!confirm('Are you sure you want to delete this partnership request? This action cannot be undone.')) {
            return;
        }
        
        try {
            // Determine which table to update
            const tableName = await determineTableName();
            
            const { error } = await supabaseClient
                .from(tableName)
                .delete()
                .eq('id', partnershipId);
            
            if (error) throw error;
            
            // Remove the partnership from the array locally
            setPartnerships(partnerships.filter(partnership => partnership.id !== partnershipId));
            
            // Close the modal if the deleted partnership is currently selected
            if (selectedPartnership && selectedPartnership.id === partnershipId) {
                handleCloseDetailsModal();
                handleCloseUpdateModal();
            }
        } catch (err) {
            console.error('Error deleting partnership:', err);
            alert('Failed to delete partnership. Please try again.');
        }
    };

    // Format date for display
    const formatDate = (dateString) => {
        if (!dateString) return 'N/A';
        
        const options = { 
            year: 'numeric', 
            month: 'short', 
            day: 'numeric',
            hour: '2-digit',
            minute: '2-digit'
        };
        return new Date(dateString).toLocaleDateString(undefined, options);
    };

    // Get status badge color based on status
    const getStatusBadgeClass = (status) => {
        switch (status) {
            case 'pending':
                return 'bg-yellow-100 text-yellow-800';
            case 'approved':
                return 'bg-green-100 text-green-800';
            case 'rejected':
                return 'bg-red-100 text-red-800';
            default:
                return 'bg-gray-100 text-gray-800';
        }
    };

    if (isLoading && partnerships.length === 0) {
        return (
            <div className="flex justify-center items-center h-64">
                <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-green-500"></div>
            </div>
        );
    }

    if (error) {
        return (
            <div className="bg-red-50 p-6 rounded-lg">
                <h2 className="text-xl font-bold text-red-700">Error</h2>
                <p className="text-red-600">{error}</p>
                <button 
                    onClick={fetchPartnerships}
                    className="mt-4 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700"
                >
                    Retry
                </button>
            </div>
        );
    }

    return (
        <div>
            <div className="flex justify-between items-center mb-6">
                <h2 className="text-2xl font-bold">Partnership Requests</h2>
                <div className="flex space-x-2">
                    <button 
                        onClick={() => setFilter('all')}
                        className={`px-3 py-1 rounded ${
                            filter === 'all' ? 
                            'bg-gray-800 text-white' : 
                            'bg-gray-200 text-gray-800 hover:bg-gray-300'
                        }`}
                    >
                        All
                    </button>
                    <button 
                        onClick={() => setFilter('pending')}
                        className={`px-3 py-1 rounded ${
                            filter === 'pending' ? 
                            'bg-yellow-500 text-white' : 
                            'bg-yellow-100 text-yellow-800 hover:bg-yellow-200'
                        }`}
                    >
                        Pending
                    </button>
                    <button 
                        onClick={() => setFilter('approved')}
                        className={`px-3 py-1 rounded ${
                            filter === 'approved' ? 
                            'bg-green-500 text-white' : 
                            'bg-green-100 text-green-800 hover:bg-green-200'
                        }`}
                    >
                        Approved
                    </button>
                    <button 
                        onClick={() => setFilter('rejected')}
                        className={`px-3 py-1 rounded ${
                            filter === 'rejected' ? 
                            'bg-red-500 text-white' : 
                            'bg-red-100 text-red-800 hover:bg-red-200'
                        }`}
                    >
                        Rejected
                    </button>
                </div>
            </div>

            {/* Partnerships Table */}
            <div className="bg-white rounded-lg shadow overflow-hidden border border-gray-200">
                <div className="overflow-x-auto">
                    <table className="min-w-full divide-y divide-gray-200">                        <thead className="bg-gray-50">
                            <tr>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Organization/Person
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Contact
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Type
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Status
                                </th>
                                <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Date
                                </th>
                                <th scope="col" className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                                    Actions
                                </th>
                            </tr>
                        </thead>
                        <tbody className="bg-white divide-y divide-gray-200">
                            {partnerships.length > 0 ? (
                                partnerships.map(partnership => (
                                    <tr key={partnership.id} className="hover:bg-gray-50">
                                        <td className="px-6 py-4 whitespace-nowrap">
                                            <div className="text-sm font-medium text-gray-900">
                                                {partnership.name || partnership.organization_name || 'Unknown'}
                                            </div>
                                            <div className="text-xs text-gray-500">
                                                {partnership.role || partnership.organization_type || partnership.skills || ''}
                                            </div>
                                        </td>                        <td className="px-6 py-4 whitespace-nowrap">
                            <div className="text-sm text-gray-900">{partnership.email}</div>
                            <div className="text-xs text-gray-500">{partnership.phone || 'No phone'}</div>
                        </td>                        <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                (partnership.formType === 'esg' || partnership.formType === 'esg_platform') ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                            }`}>
                                {(partnership.formType === 'esg' || partnership.formType === 'esg_platform') ? 'ESG Platform' : 'Partnership'}
                            </span>
                        </td>
                        <td className="px-6 py-4 whitespace-nowrap">
                            <span className={`px-2 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(partnership.status)}`}>
                                {partnership.status || 'pending'}
                            </span>
                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                            {formatDate(partnership.created_at)}
                                        </td>
                                        <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                                            <button 
                                                onClick={() => handleViewDetails(partnership)}
                                                className="text-blue-600 hover:text-blue-900 mr-3"
                                                title="View Details"
                                            >
                                                <i className="fas fa-eye"></i>
                                            </button>
                                            <button 
                                                onClick={() => handleOpenUpdateModal(partnership)}
                                                className="text-green-600 hover:text-green-900 mr-3"
                                                title="Update Status"
                                            >
                                                <i className="fas fa-edit"></i>
                                            </button>
                                            <button 
                                                onClick={() => handleDeletePartnership(partnership.id)}
                                                className="text-red-600 hover:text-red-900"
                                                title="Delete"
                                            >
                                                <i className="fas fa-trash"></i>
                                            </button>
                                        </td>
                                    </tr>
                                ))
                            ) : (                                <tr>
                                    <td colSpan="6" className="px-6 py-4 text-center text-gray-500">
                                        {filter === 'all' ? (
                                            'No partnership requests found.'
                                        ) : (
                                            `No ${filter} partnership requests found.`
                                        )}
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </table>
                </div>
            </div>

            {/* View Partnership Details Modal */}
            {showDetailsModal && selectedPartnership && (
                <div className="fixed inset-0 overflow-y-auto bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-xl w-full p-6">                        <div className="flex justify-between items-center mb-4">                            <h3 className="text-xl font-bold">
                                {(selectedPartnership.formType === 'esg' || selectedPartnership.formType === 'esg_platform') ? 'ESG Platform Request' : 'Partnership Request'} Details
                            </h3>
                            <button 
                                onClick={handleCloseDetailsModal}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        
                        <div className="mb-6">
                            <div className="bg-gray-100 p-4 rounded-lg mb-4">
                                <h4 className="text-lg font-semibold mb-2">Request Information</h4>                                <p><strong>Request Type:</strong> 
                                    <span className={`ml-2 px-2 py-1 text-xs rounded-full ${
                                        (selectedPartnership.formType === 'esg' || selectedPartnership.formType === 'esg_platform') ? 'bg-purple-100 text-purple-800' : 'bg-blue-100 text-blue-800'
                                    }`}>
                                        {(selectedPartnership.formType === 'esg' || selectedPartnership.formType === 'esg_platform') ? 'ESG Platform Access' : 'Partnership Inquiry'}
                                    </span>
                                </p>
                                <p><strong>Name:</strong> {selectedPartnership.name || selectedPartnership.organization_name || 'N/A'}</p>
                                <p><strong>Email:</strong> {selectedPartnership.email || 'N/A'}</p>
                                <p><strong>Phone:</strong> {selectedPartnership.phone || 'N/A'}</p>
                                {selectedPartnership.website && <p><strong>Website:</strong> {selectedPartnership.website}</p>}
                                {selectedPartnership.organization_type && <p><strong>Organization Type:</strong> {selectedPartnership.organization_type}</p>}
                                {selectedPartnership.company && <p><strong>Company:</strong> {selectedPartnership.company}</p>}
                                {selectedPartnership.role && <p><strong>Role:</strong> {selectedPartnership.role}</p>}
                                {selectedPartnership.skills && <p><strong>Skills:</strong> {selectedPartnership.skills}</p>}
                                {selectedPartnership.experience && <p><strong>Experience:</strong> {selectedPartnership.experience}</p>}
                            </div>
                            
                            {(selectedPartnership.message || selectedPartnership.motivation) && (
                                <div className="bg-gray-100 p-4 rounded-lg mb-4">
                                    <h4 className="text-lg font-semibold mb-2">Message</h4>
                                    <p className="whitespace-pre-line">{selectedPartnership.message || selectedPartnership.motivation || ''}</p>
                                </div>
                            )}
                            
                            <div className="bg-gray-100 p-4 rounded-lg">
                                <h4 className="text-lg font-semibold mb-2">Status Information</h4>
                                <p>
                                    <strong>Status:</strong> 
                                    <span className={`ml-2 px-2 py-1 text-xs rounded-full ${getStatusBadgeClass(selectedPartnership.status)}`}>
                                        {selectedPartnership.status || 'pending'}
                                    </span>
                                </p>
                                <p><strong>Submitted:</strong> {formatDate(selectedPartnership.created_at)}</p>
                                <p><strong>Last Updated:</strong> {formatDate(selectedPartnership.updated_at)}</p>
                                <p><strong>Admin Notes:</strong> {selectedPartnership.admin_notes || 'No notes'}</p>
                            </div>
                        </div>
                        
                        <div className="flex justify-end">
                            <button 
                                onClick={handleCloseDetailsModal}
                                className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 mr-2 hover:bg-gray-50"
                            >
                                Close
                            </button>
                            <button 
                                onClick={() => {
                                    handleCloseDetailsModal();
                                    handleOpenUpdateModal(selectedPartnership);
                                }}
                                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                            >
                                Update Status
                            </button>
                        </div>
                    </div>
                </div>
            )}

            {/* Update Status Modal */}
            {showUpdateModal && selectedPartnership && (
                <div className="fixed inset-0 overflow-y-auto bg-gray-800 bg-opacity-75 flex items-center justify-center z-50">
                    <div className="bg-white rounded-lg shadow-xl max-w-md w-full p-6">
                        <div className="flex justify-between items-center mb-4">
                            <h3 className="text-xl font-bold">Update Partnership Status</h3>
                            <button 
                                onClick={handleCloseUpdateModal}
                                className="text-gray-500 hover:text-gray-700"
                            >
                                <i className="fas fa-times"></i>
                            </button>
                        </div>
                        
                        <form onSubmit={handleUpdatePartnership}>
                            <div className="mb-4">
                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                    Status
                                </label>
                                <select 
                                    value={updateStatus}
                                    onChange={(e) => setUpdateStatus(e.target.value)}
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    required
                                >
                                    <option value="pending">Pending</option>
                                    <option value="approved">Approved</option>
                                    <option value="rejected">Rejected</option>
                                </select>
                            </div>
                            
                            <div className="mb-6">
                                <label className="block text-gray-700 text-sm font-bold mb-2">
                                    Admin Notes
                                </label>
                                <textarea 
                                    value={updateNotes}
                                    onChange={(e) => setUpdateNotes(e.target.value)}
                                    rows="4"
                                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-blue-500 focus:border-blue-500"
                                    placeholder="Add notes about this partnership request (visible only to admins)"
                                ></textarea>
                            </div>
                            
                            <div className="flex justify-end">
                                <button 
                                    type="button"
                                    onClick={handleCloseUpdateModal}
                                    className="px-4 py-2 border border-gray-300 rounded-md text-gray-700 mr-2 hover:bg-gray-50"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit"
                                    className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                                >
                                    Save Changes
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
}

export default AdminPartnerships;